import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import {
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { FilterData } from '../../admin-strategy';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-strategy-details',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzFormItemComponent,
    NzInputModule,
    NzSelectModule,
    NzRadioModule,
    NzCheckboxModule,
    ReactiveFormsModule,
    CommonModule,
  ],
  templateUrl: './strategy-details.component.html',
})
export class StrategyDetailsComponent implements OnInit, OnDestroy {
  @Input() languageLevel: FilterData[] = [];
  @Input() behaviourResuorceType: FilterData[] = [];
  @Input() behaviourReplacementType: FilterData[] = [];
  @Input() behaviourSupervisionType: FilterData[] = [];

  @Input() detailsForm!: FormGroup;

  readonly maxTitleLength = 100;
  readonly maxDescriptionLength = 4000;
  readonly REPLACEMENT_BEHAVIOR_TYPE_ID = 71;

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.setupReplacementTypeValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupReplacementTypeValidation(): void {
    const resourceTypeControl = this.detailsForm.get('resourceType');
    const replacementTypeControl = this.detailsForm.get('replacementType');

    if (!resourceTypeControl || !replacementTypeControl) return;

    resourceTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(resourceTypeId => {
        this.updateReplacementTypeValidation(
          resourceTypeId,
          replacementTypeControl
        );
      });

    // Initial setup
    this.updateReplacementTypeValidation(
      resourceTypeControl.value,
      replacementTypeControl
    );
  }

  private updateReplacementTypeValidation(
    resourceTypeId: number | null,
    replacementTypeControl: FormGroup | AbstractControl
  ): void {
    if (resourceTypeId === this.REPLACEMENT_BEHAVIOR_TYPE_ID) {
      // For Replacement Behavior type, make Replacement Type required
      replacementTypeControl.setValidators([Validators.required]);
      replacementTypeControl.enable();
    } else {
      // For other types, not required
      replacementTypeControl.setValidators([]);
      replacementTypeControl.reset();
    }

    replacementTypeControl.updateValueAndValidity({ emitEvent: false });
  }

  getCharacterCount(controlName: string): string {
    const control = this.detailsForm.get(controlName);
    const currentLength = control?.value?.length || 0;
    return `${currentLength} of ${this.maxDescriptionLength} characters remaining`;
  }

  getError(controlName: string): string {
    const control = this.detailsForm.get(controlName);
    if (!control?.errors || (!control.dirty && !control.touched)) return '';

    const errorMessages: {
      [key in
        | 'resourceType'
        | 'replacementType'
        | 'title'
        | 'description'
        | 'languageLevel'
        | 'supervisionType']: string;
    } = {
      resourceType: 'Please select a resource type',
      replacementType: 'Please select at least one replacement type',
      title: control.errors['maxlength']
        ? `Title cannot exceed ${this.maxTitleLength} characters`
        : 'Title is required',
      description: control.errors['maxlength']
        ? `Description cannot exceed ${this.maxDescriptionLength} characters`
        : 'Description is required',
      languageLevel: 'Please select a language level',
      supervisionType: 'Please select a supervision type',
    };

    return (
      errorMessages[controlName as keyof typeof errorMessages] ||
      'Invalid input'
    );
  }

  isInvalid(controlName: string): boolean {
    const control = this.detailsForm.get(controlName);
    return !!control?.errors && (control.dirty || control.touched);
  }

  trackByFn(index: number, item: FilterData): number {
    return item.id;
  }
}
